import type { components } from 'admination-react/api-schema'
import { fillPath, getJson, postForBlob, sendJson } from '@/core/api/http'
import {
  aufnahmePotenzialanalyseProbleme,
  isAufnahmePotenzialanalyse,
  type AufnahmePotenzialanalyseProblem,
} from './aufnahmePotenzialanalyse'
import { isSprachniveauAbweichung } from './spalteStand'

type Schemas = components['schemas']

export type Foerderplan = Schemas['FoerderplanStandResult']

export type FoerderplanGespeichert = Schemas['FoerderplanSpeichernResult']

type WerteBody = Schemas['VorschauRequest']

export type FoerderplanProblem = AufnahmePotenzialanalyseProblem

const MALFORMED_RESPONSE = 'Die Antwort des Servers hatte nicht die erwartete Form.'

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

export async function getFoerderplan(coachingId: string): Promise<Foerderplan> {
  const body = await getJson<unknown>(fillPath('/foerderplan/{coaching_id}', { coaching_id: coachingId }))
  if (
    !isAufnahmePotenzialanalyse(body) ||
    !isRecord((body as Record<string, unknown>).hinweise) ||
    !isSprachniveauAbweichung((body as Record<string, unknown>).sprachniveau_abweichung)
  ) {
    throw new Error(MALFORMED_RESPONSE)
  }
  return body as Schemas['FoerderplanStandResult']
}

export async function fetchFoerderplanVorschau(coachingId: string, werte: Record<string, unknown>): Promise<Blob> {
  const path = fillPath('/foerderplan/{coaching_id}/vorschau', { coaching_id: coachingId })
  return postForBlob(path, { werte } satisfies WerteBody, 'application/pdf')
}

function isGespeichert(body: unknown): body is FoerderplanGespeichert {
  return (
    isRecord(body) &&
    typeof body.dokument_id === 'string' &&
    typeof body.status === 'string' &&
    typeof body.aufgabe === 'string'
  )
}

export async function saveFoerderplan(
  coachingId: string,
  werte: Record<string, unknown>,
): Promise<FoerderplanGespeichert> {
  const path = fillPath('/foerderplan/{coaching_id}', { coaching_id: coachingId })
  const body = await sendJson<unknown>(path, 'POST', { werte } satisfies WerteBody)
  if (!isGespeichert(body)) throw new Error(MALFORMED_RESPONSE)
  return body
}

export async function speichereFoerderplanEntwurf(coachingId: string, werte: Record<string, unknown>): Promise<void> {
  const path = fillPath('/foerderplan/{coaching_id}/entwurf', { coaching_id: coachingId })
  await sendJson<void>(path, 'PUT', { werte } satisfies WerteBody)
}

export function foerderplanProbleme(error: unknown): FoerderplanProblem[] | null {
  return aufnahmePotenzialanalyseProbleme(error)
}
